import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useHasAccount } from '../../state/authStore';

interface SignUpLinkProps {
  className?: string;
  /** The invitation a stranger reads. */
  children: ReactNode;
  /** Runs for a stranger only — the calculator uses it to hand its figures to the wizard. */
  onClick?: () => void;
  /** Where somebody who already has an account goes instead. */
  accountTo?: string;
  /** What that link says. Defaults to "Go to dashboard". */
  accountLabel?: ReactNode;
}

/**
 * Every "create an account" button on the public site.
 *
 * For a stranger it is the sign-up link it always was. For somebody already signed in it is the way
 * back into their account, because offering a second account to a person who has one is how they
 * end up believing the first was lost. It deliberately shows nothing about the account itself —
 * these pages may carry an analytics tag, and the rule is that no such page shows personal data.
 */
export function SignUpLink({ className, children, onClick, accountTo = '/dashboard', accountLabel }: SignUpLinkProps) {
  const { t } = useTranslation();
  const hasAccount = useHasAccount();

  if (hasAccount) {
    return (
      <Link className={className} to={accountTo}>
        {accountLabel ?? t('marketing.nav.toApp')}
      </Link>
    );
  }

  return (
    <Link className={className} to="/register" onClick={onClick}>
      {children}
    </Link>
  );
}
